<template>
	<div ref="createRDIRef" id="rdi-test">
		<v-dialog
			v-model="modalStatus"
			:max-width="$vuetify.breakpoint.mobile ? undefined : '780'"
			:fullscreen="$vuetify.breakpoint.mobile"
			scrollable
			persistent
		>
			<v-card class="modal-card" elevation="0">
				<!-- Header -->
				<div class="modal-header">
					<div class="modal-header-left">
						<div class="modal-icon">
							<v-icon color="white" size="16">mdi-file-document-multiple-outline</v-icon>
						</div>
						<div>
							<p class="modal-title">Nueva RDI</p>
							<p class="modal-subtitle">
								<span>{{ $store.userStore.state.userInfo.name }}</span>
								<span class="separator">·</span>
								<span>{{ $store.projectsStore.state.selectedProject.name }}</span>
							</p>
						</div>
					</div>
					<div class="modal-header-actions">
						<button class="header-btn minimize" @click="minimizeForm" title="Minimizar">
							<v-icon size="14" color="white">mdi-window-minimize</v-icon>
						</button>
						<button class="header-btn close" @click="closeModalSelf()" title="Cerrar">
							<v-icon size="14" color="white">mdi-close</v-icon>
						</button>
					</div>
				</div>

				<v-card-text class="modal-body pa-0">
					<v-form ref="form" v-model="validform">

						<!-- Título -->
						<div class="form-section">
							<v-text-field
								v-model="newRDIForm.title"
								placeholder="Título de la RDI"
								outlined
								required
								hide-details
								:rules="requiredRule"
								:dense="true"
								class="title-input"
								@keydown="sendToSearch"
							>
								<template slot="append">
									<v-progress-circular
										v-if="searching"
										:size="16"
										:width="2"
										color="#2D4B95"
										indeterminate
									/>
									<button
										v-else-if="searchResultsCount > 0"
										class="similares-btn"
										:class="{ 'is-open': showResults }"
										type="button"
										@click.stop="showResults = !showResults"
									>
										<span class="similares-count">{{ searchResultsCount }}</span>
										Similares
										<v-icon size="12" class="similares-chevron">
											{{ showResults ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
										</v-icon>
									</button>
								</template>
							</v-text-field>
							<SearchBlockForms v-if="showResults" :searching="searching" :searchResult="searchResult" :closeResults="closeResults"/>
						</div>

						<!-- Descripción -->
						<div class="form-section">
							<label class="field-label">Descripción</label>
							<InteractionEditor
								v-model="newRDIForm.description"
								item_type="rdi"
								:allow-inline-images="false"
								@paste-image="onPasteImage"
								@mentions-updated="onMentionsUpdated"
							/>
							<!-- Adjuntos -->
							<div class="attachments-row">
								<div class="previews">
									<div v-for="(file, index) in filesPreview" :key="index" class="preview-item">
										<div v-if="file.src" class="preview-img-wrap">
											<button class="preview-remove" type="button" @click="removeAttachment(file, index)">
												<v-icon size="10" color="white">mdi-close</v-icon>
											</button>
											<img :src="file.src" class="preview-thumb"/>
										</div>
										<div v-else class="preview-file">
											<v-icon size="14" color="#5E85EF">mdi-file-outline</v-icon>
											<span>{{ file.name }}</span>
											<button type="button" @click="removeAttachment(file, index)"><v-icon size="12" color="#9CA3AF">mdi-close</v-icon></button>
										</div>
									</div>
								</div>
								<button class="attach-btn" type="button" @click="onButtonClick">
									<v-icon size="14" color="#5E85EF">mdi-paperclip</v-icon>
									Adjuntar
								</button>
								<input ref="uploader" multiple class="d-none" type="file" @change="onFilesChanged">
							</div>
						</div>

						<!-- Grid: Prioridad + Materia + Fecha Crítica -->
						<div class="form-section">
							<div class="fields-grid three-col">

								<!-- Prioridad -->
								<div class="field-group">
									<label class="field-label">Prioridad</label>
									<div class="priority-group">
										<button
											class="priority-btn"
											:class="{ active: newRDIForm.priority === 3 }"
											type="button"
											style="--p-color: #F4516C;"
											@click="newRDIForm.priority = 3"
										>
											<span class="priority-dot"></span> Inmediata
										</button>
										<button
											class="priority-btn"
											:class="{ active: newRDIForm.priority === 2 }"
											type="button"
											style="--p-color: #fb8c00;"
											@click="newRDIForm.priority = 2"
										>
											<span class="priority-dot"></span> Media
										</button>
										<button
											class="priority-btn"
											:class="{ active: newRDIForm.priority === 1 }"
											type="button"
											style="--p-color: #3EBFA3;"
											@click="newRDIForm.priority = 1"
										>
											<span class="priority-dot"></span> Estándar
										</button>
									</div>
								</div>

								<!-- Materia -->
								<div class="field-group">
									<label class="field-label">Materia</label>
									<v-combobox
										v-model="newRDIForm.subject"
										:loading="$store.projectsStore.state.loadingProjectSubjects"
										:items="$store.projectsStore.state.projectSubjects"
										item-text="name"
										item-value="id"
										cache-items
										outlined
										dense
										required
										:rules="requiredRule"
										hide-details
										placeholder="Seleccione materia"
									/>
								</div>

								<!-- Fecha Crítica -->
								<div class="field-group">
									<label class="field-label">Fecha crítica</label>
									<v-menu v-model="datePicker" :close-on-content-click="false" max-width="290">
										<template v-slot:activator="{ on, attrs }">
											<v-text-field
												:value="newRDIForm.critical_date"
												placeholder="Sin fecha"
												prepend-inner-icon="mdi-calendar"
												readonly
												v-bind="attrs"
												v-on="on"
												outlined
												dense
												hide-details
												class="remove-bottom-details"
											/>
										</template>
										<v-date-picker locale="es" v-model="newRDIForm.critical_date" @change="datePicker = false" />
									</v-menu>
								</div>

							</div>
						</div>

						<!-- Asociaciones -->
						<div class="form-section">
							<label class="field-label">Asociaciones</label>
							<ItemsAssociationInputSelect ref="itemsAssociationInputSelectRef" :changeItems="changeAssociations"/>
						</div>

						<!-- Grid: Especialidades + Plano Referencia -->
						<div class="form-section">
							<div class="fields-grid two-col">

								<!-- Especialidades -->
								<div class="field-group">
									<label class="field-label">Especialidades</label>
									<v-select
										v-model="especialidades"
										:items="$store.projectsStore.state.selectedProject.specialties"
										item-text="name"
										item-value="id"
										placeholder="Seleccione especialidades"
										outlined
										dense
										hide-details
										multiple
										:rules="requiredRule"
										required
									/>
								</div>

								<!-- Participantes / Subscriptores -->
								<div>
									<div class="field-group">
										<label class="field-label">Participantes / Suscriptores</label>
										<v-autocomplete
											v-model="subscriptions"
											:loading="$store.usersStore.state.loadingUserList"
											:items="getFinalUserList(true)"
											:search-input.sync="searchUsersParticipants"
											item-text="name"
											item-value="id"
											:filter="usersFilter"
											outlined
											dense
											hide-details
											multiple
											hide-selected
											placeholder="Agregar participantes"
										>
											<template v-slot:item="data">
												<v-list-item-avatar size="28">
													<img v-if="data.item.info && data.item.info.avatar" :src="data.item.info.avatar">
													<v-icon v-else size="20">mdi-account</v-icon>
												</v-list-item-avatar>
												<v-list-item-content>
													<v-list-item-title>{{ data.item.name }} <span class="light-blue-text">{{ data.item.projectjob.userjobtype.name }}</span></v-list-item-title>
													<v-list-item-subtitle>{{ data.item.email }}</v-list-item-subtitle>
												</v-list-item-content>
											</template>
											<template v-slot:selection="{}"></template>
										</v-autocomplete>
									</div>
									<!-- Tarjetas de participantes -->
									<div v-if="subscriptions.length" class="participant-list mt-2">
										<div v-for="id in subscriptions" :key="id" class="participant-card">
											<template v-if="getUserByID(id)">
												<div class="participant-left">
													<div class="p-avatar">
														<img v-if="getUserByID(id).info && getUserByID(id).info.avatar" :src="getUserByID(id).info.avatar" />
														<span v-else>{{ initials(getUserByID(id).name) }}</span>
													</div>
													<div class="p-info">
														<span class="p-name">{{ getUserByID(id).name }}</span>
														<span class="p-role" v-if="getUserByID(id).projectjob && getUserByID(id).projectjob.userjobtype">{{ getUserByID(id).projectjob.userjobtype.name }}</span>
													</div>
												</div>
												<div class="participant-right">
													<button class="p-remove-btn" type="button" @click="removeParticipantByID(id)">
														<v-icon size="14" color="#9CA3AF">mdi-trash-can-outline</v-icon>
													</button>
												</div>
											</template>
										</div>
									</div>
								</div>
							</div>
						</div>

						<!-- Enviar Copia -->
						<!-- <div class="form-section">
							<div class="section-row">
								<label class="field-label">Enviar copia</label>
								<v-tooltip bottom>
									<template v-slot:activator="{ on, attrs }">
										<button
											class="toggle-pill"
											:class="{ active: attachFiles }"
											type="button"
											:disabled="disabledAttachSelector"
											v-bind="attrs"
											v-on="on"
											@click="attachFiles = !attachFiles"
										>
											<v-icon size="12">{{ attachFiles ? 'mdi-check-circle-outline' : 'mdi-checkbox-blank-circle-outline' }}</v-icon>
											Con adjuntos
										</button>
									</template>
									<span>Se adjuntarán los archivos en el correo de notificación</span>
								</v-tooltip>
							</div>
							<v-combobox
								v-model="copy_to"
								:loading="$store.usersStore.state.loadingUserList"
								:items="$store.usersStore.state.usersList"
								:search-input.sync="searchUsers"
								item-text="name"
								item-value="id"
								outlined
								dense
								multiple
								hide-details
								placeholder="Usuarios o correos a copiar"
								return-object
								taggable
							>
								<template v-slot:item="data">
									<v-list-item-avatar size="28">
										<img v-if="data.item.info && data.item.info.avatar" :src="data.item.info.avatar">
										<v-icon v-else size="20">mdi-account</v-icon>
									</v-list-item-avatar>
									<v-list-item-content>
										<v-list-item-title>{{ data.item.name }} <span class="light-blue-text">{{ data.item.projectjob.userjobtype.name }}</span></v-list-item-title>
										<v-list-item-subtitle>{{ data.item.email }}</v-list-item-subtitle>
									</v-list-item-content>
								</template>
							</v-combobox>
						</div> -->

						<!-- Recordatorio -->
						<div class="form-section">
							<div class="field-group">
								<v-tooltip bottom>
									<template v-slot:activator="{ on, attrs }">
										<label class="field-label" v-bind="attrs" v-on="on">
											Recordatorio <v-icon size="13" color="#9CA3AF">mdi-information-outline</v-icon>
										</label>
									</template>
									<span>Se agregará esta RDI a tus recordatorios semanales</span>
								</v-tooltip>
								<div class="weekday-group">
									<button
										v-for="(day, index) in weekDays"
										:key="index"
										class="weekday-btn"
										:class="{ active: isWeekDaySelected(index) }"
										type="button"
										@click="addWeekDay(index)"
									>
										{{ day.substring(0, 2) }}
									</button>
								</div>
							</div>
						</div>

						<!-- Footer -->
						<div class="modal-footer">
							<div></div>
							<div class="footer-actions">
								<button class="btn-cancel" type="button" @click="closeModalSelf()">Cancelar</button>
								<button
									v-if="checkPermissions(['create_rdi'])"
									class="btn-confirm"
									:class="{ canceling: inCancelTime }"
									type="button"
									:disabled="loadingData"
									@click="preCreateRdi()"
								>
									<v-progress-circular v-if="loadingData" :size="14" :width="2" color="white" indeterminate class="mr-1" />
									{{ confirmButtonText }}
								</button>
								<button
									v-else
									class="btn-confirm"
									:class="{ canceling: inCancelTime }"
									type="button"
									:disabled="loadingData"
									@click="saveDraftAndClose()"
								>
									<v-progress-circular v-if="loadingData" :size="14" :width="2" color="white" indeterminate class="mr-1" />
									Guardar Borrador
								</button>
							</div>
						</div>

					</v-form>
				</v-card-text>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import _debounce from 'lodash/debounce'
import {create, getRdiByID} from '@/helpers/api/rdi'
import {search} from '@/helpers/api/general'
import {deleteFile} from '@/helpers/api/fileManager'
import {create as createDraftAPI, update as updateDraftAPI} from '@/helpers/api/drafts'
import EventBus from '@/helpers/event_bus'
import SearchBlockForms from '@/components/Globals/Headers/SearchBlockForms'
import ItemsAssociationInputSelect from '@/components/Globals/Helpers/ItemsAssociationInputSelect'
import InteractionEditor from '@/components/Globals/Helpers/InteractionEditor'

export default {
	name: 'CreateRDIModal',
	components: { InteractionEditor, SearchBlockForms, ItemsAssociationInputSelect },
	props: {
		modalStatus: { type: Boolean, default: false },
		closeModal: { type: Function, required: true },
		draftToLoad: { type: Object, default: null }
	},
	watch: {
		searchUsers(val) {
			if(val && val.length > 5) this.searchUsersLocal(val)
		},
		draftToLoad(newVal) {
			if(newVal) this.loadDraft(newVal)
		},
		filesPreview() {
			this.checkFilesAttach()
		},
		newRDIForm: {
			handler() { this.handleFormChange() },
			deep: true
		},
		especialidades: {
			handler() { this.handleFormChange() },
			deep: true
		},
		copy_to: {
			handler() { this.handleFormChange() },
			deep: true
		},
		associations: {
			handler() { this.handleFormChange() },
			deep: true
		}
		,
		subscriptions: {
			handler() { this.handleFormChange() },
			deep: true
		}
	},
	data: () => ({
		validform: true,
		newRDIForm: {
			title: '',
			priority: 1,
			description: '',
				especialidades: null,
			critical_date: null,
			files: [],
			copy_to: null,
			plano_ref: null,
			reminder: [],
			reminder_repeatable: true,
			subject: null
		},
		associations: [],
		subscriptions: [],
		loadingUsers: false,
		usersToSelect: [],
		searchUsersParticipants: null,
		attachFiles: false,
		disabledAttachSelector: true,
		especialidades: [],
		copy_to: [],
		datePicker: false,
		requiredRule: [v => !!v || 'Campo requerido'],
		filesPreview: [],
		loadingData: false,
		searchUsers: null,
		showResults: false,
		searchResultsCount: 0,
		searching: false,
		searchResult: {},
		cancelTimer: 7,
		inCancelTime: false,
		cancelInterval: null,
		confirmButtonText: 'Confirmar',
		weekDays: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'],
		draftId: null,
		draftCreated: false,
		draftUpdateInterval: 20,
		draftSaveTimer: null,
		handleFormChangeDebounced: null,
		isResetting: false,
		newInteractionMentions: []
	}),
	beforeUnmount() {
		if(this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
	},
	mounted() {
		this.handleFormChangeDebounced = _debounce(() => this.checkAndCreateDraft(), 1000)
		if(this.$store.rdiStore.state.borradorRDI) {
			this.newRDIForm = this.$store.rdiStore.state.borradorRDI
			this.$store.rdiStore.commit('setBorradorRDI', null)
		}
		if(this.$store.projectsStore.state.projectSubjects.length == 0) this.getProjectSubjects()
		if(this.$store.usersStore.state.usersList.length === 0) this.getUsersList()
		if(this.$store.rdiStore.state.initialDataRDI) {
			this.newRDIForm = {
				task_id: this.$store.rdiStore.state.initialDataRDI.task_id,
				title: this.$store.rdiStore.state.initialDataRDI.title,
				priority: 1,
				description: this.$store.rdiStore.state.initialDataRDI.description,
				especialidades: null,
				critical_date: null,
				files: [],
				copy_to: null,
				plano_ref: null,
				reminder: [],
				reminder_repeatable: true,
				subject: null
			}
			if (this.$store.rdiStore.state.initialDataRDI.associations && this.$store.rdiStore.state.initialDataRDI.associations.length > 0) {
				this.associations = this.$store.rdiStore.state.initialDataRDI.associations
				this.$refs.itemsAssociationInputSelectRef.setSelectedItems(this.associations)
			}
			this.$store.rdiStore.commit('setInitialDataRDI', null)
		}
		if(this.draftToLoad) this.loadDraft(this.draftToLoad)
		this.fileListeners()
	},
	methods: {
		async onPasteImage(file) { await this.createDraft(); this.onFileChanged(null, file) },
		onMentionsUpdated(userIds) { this.newInteractionMentions = userIds },
		handleFormChange() {
			if(!this.isResetting && this.handleFormChangeDebounced) this.handleFormChangeDebounced()
		},
		checkAndCreateDraft() {
			if(!this.draftCreated && !this.draftId) this.createDraft()
		},
		async createDraft() {
			if(this.draftCreated) return
			this.draftCreated = true
			const draftData = {
				project_id: this.$store.projectsStore.state.selectedProject.id,
				type: 2,
				data: JSON.stringify({ form: this.newRDIForm, especialidades: this.especialidades, subscriptions: this.subscriptions, /* copy_to: this.copy_to, */ associations: this.associations })
			}
			try {
				const response = await createDraftAPI(draftData)
				if(response.code === 200) { this.draftId = response.draft.id; this.startDraftUpdateTimer(); return this.draftId }
        else if(response.offline) {
					this.$store.globalStore.dispatch('addMessage', {
						message: 'No hay conexión. El borrador se mantendrá localmente y podrás retomarlo cuando vuelvas a tener conexión desde la sección de borradores.',
						title: 'Borrador guardado localmente',
						created: new Date(),
						type: 'warning',
						icon: 'mdi-wifi-off',
						color: '#F59E0B'
					})
					this.draftId = response.draft.id
					this.startDraftUpdateTimer()
					return this.draftId
        }
			} catch(error) { console.error('Error al crear borrador:', error); this.draftCreated = false; throw error }
		},
		startDraftUpdateTimer() {
			if(this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
			this.draftSaveTimer = setInterval(() => this.updateDraft(), this.draftUpdateInterval * 1000)
		},
		async updateDraft() {
			if(!this.draftId) return
			try {
				await updateDraftAPI({ id: this.draftId, type: 2, data: JSON.stringify({ form: this.newRDIForm, especialidades: this.especialidades, subscriptions: this.subscriptions, /* copy_to: this.copy_to, */ associations: this.associations }) })
			} catch(error) { console.error('Error al actualizar borrador:', error) }
		},
		closeResults() { this.showResults = false; this.searching = false },
		changeAssociations(associations) { this.associations = associations.map(a => ({ type: a.type, id: a.id })) },
		sendToSearch: _debounce(function() { this.searchForString(this.newRDIForm.title) }, 800),
		async searchForString(searchString) {
			this.searching = true
			const searchResult = await search({ search: searchString, project_id: this.$store.projectsStore.state.selectedProject.id, exclude_description: true })
			if(searchResult.code == 200) { this.searchResult = searchResult; this.searchResultsCount = searchResult.tasks.length + searchResult.rdis.length }
			this.searching = false
		},
		addWeekDay(index) {
			const found = this.newRDIForm.reminder.findIndex(i => i === index)
			if(found !== -1) this.newRDIForm.reminder.splice(found, 1)
			else this.newRDIForm.reminder.push(index)
			if(this.newRDIForm.reminder.length === 0) this.newRDIForm.reminder_repeatable = false
		},
		isWeekDaySelected(index) { return this.newRDIForm.reminder.includes(index) },
		minimizeForm() {
			this.updateDraft()
			let title = 'Borrador RDI: ' + (this.newRDIForm.title || 'Sin Título')
			this.$store.globalStore.commit('addTaskToTasksHandler', { task_id: uuidv4(), title, type: 'RDI', type_id: 4, number: null, newRDIForm: this.newRDIForm, draft_id: this.draftId })
			this.closeModalSelf()
		},
		closeModalSelf() {
			if(this.handleFormChangeDebounced) this.handleFormChangeDebounced.cancel()
			this.updateDraft()
			this.isResetting = true
			if(this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
			this.closeModal()
			this.newRDIForm = { title: '', priority: 1, description: '', especialidades: null, critical_date: null, files: [], copy_to: null, plano_ref: null, reminder: [], reminder_repeatable: true, subject: null }
			this.associations = []; this.especialidades = []; this.copy_to = []; this.filesPreview = []
			this.subscriptions = []; this.searchUsersParticipants = null; this.loadingUsers = false; this.usersToSelect = []
			this.searchUsers = null; this.showResults = false; this.searchResultsCount = 0
			this.searching = false; this.searchResult = {}; this.datePicker = false
			this.loadingData = false; this.draftId = null; this.draftCreated = false; this.newInteractionMentions = []
			this.$nextTick(() => setTimeout(() => { this.isResetting = false }, 100))
		},
		async loadDraft(draft) {
			if(!draft) return
			this.isResetting = true
			if(this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
			let draftData = draft.data
			if(typeof draftData === 'string') { try { draftData = JSON.parse(draftData) } catch(e) { this.isResetting = false; return } }
			if(draftData.form) this.newRDIForm = {...this.newRDIForm, ...draftData.form}
			if(draftData.especialidades && Array.isArray(draftData.especialidades)) this.especialidades = [...draftData.especialidades]
			if(draftData.copy_to && Array.isArray(draftData.copy_to)) this.copy_to = [...draftData.copy_to]
			if(draftData.associations) this.associations = [...draftData.associations]
			if(draftData.subscriptions && Array.isArray(draftData.subscriptions)) this.subscriptions = [...draftData.subscriptions]
			try {
				const filePreviews = []
				if(draft.files && Array.isArray(draft.files)) {
					for(const file of draft.files) {
						if(file.mime_type && file.mime_type.includes('image')) filePreviews.push(file.pathmedia?.path ? {src: file.pathmedia.path, id: file.id, name: file.name} : {noImage: true, id: file.id, name: file.name})
						else filePreviews.push({noImage: true, id: file.id, name: file.name})
					}
				}
				this.filesPreview = filePreviews
			} catch(e) { console.error('Error al cargar archivos del draft:', e) }
			this.draftId = draft.id; this.draftCreated = true; this.startDraftUpdateTimer()
			this.$nextTick(() => { this.isResetting = false })
			this.$store.globalStore.dispatch('addMessage', { message: 'Borrador cargado correctamente.', title: 'Borrador Cargado', created: new Date(), type: 'success', icon: 'mdi-check-circle', color: '#3EBFA3' })
		},
		async getProjectSubjects() { this.$store.projectsStore.dispatch('getProjectSubjects', this.$store.projectsStore.state.selectedProject.id) },
		async getUsersList() { this.$store.usersStore.dispatch('getUsersList', { project_id: this.$store.projectsStore.state.selectedProject.id, start: 0, limit: 20 }) },
		async getRdiList() {
			let project_id = this.$store.projectsStore.state.selectedProject?.id || parseInt(localStorage.getItem('selectedProjectID'))
			if(project_id) this.$store.rdiStore.dispatch('getRDI', {project_id})
		},
		searchUsersLocal(searchString) {
			this.loadingUsers = true
			this.usersToSelect = this.$store.usersStore.state.usersList.filter(u => u.name.includes(searchString) || u.email.includes(searchString))
			this.loadingUsers = false
		},
		usersFilter(item, queryText) {
			const q = String(queryText || '').toLowerCase()
			return item.name.toLowerCase().includes(q) || item.email.toLowerCase().includes(q)
		},
		getFinalUserList(participants = false) {
			// if(this.checkPermissions && this.checkPermissions(['assign_responsable_task'])) {
			// 	return participants
			// 		? this.$store.usersStore.state.usersList.filter(u => u.id !== this.$store.userStore.state.userInfo.id)
			// 		: this.$store.usersStore.state.usersList
			// }
			// return this.$store.usersStore.state.usersList.filter(u => participants ? u.id !== this.$store.userStore.state.userInfo.id : u && ((u.projectjob && u.projectjob.job_type_id == 2) || u.id == this.$store.userStore.state.userInfo.id))

			return participants ? this.$store.usersStore.state.usersList.filter(u => u.id !== this.$store.userStore.state.userInfo.id) : this.$store.usersStore.state.usersList
		},
		getUserByID(userID) { return userID ? (this.$store.usersStore.state.usersList.find(u => u.id === userID) || null) : null },
		initials(name) { return (name || '').split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') },
		removeParticipantByID(id) {
			const idx = this.subscriptions.indexOf(id)
			if(idx !== -1) this.subscriptions.splice(idx, 1)
		},
		onButtonClick() { this.$refs.uploader.click() },
		async onFilesChanged(e) {
			await this.createDraft();
			const files = e?.target?.files || []
			for (let i = 0; i < files.length; i++) {
				await this.onFileChanged(null, files[i])
			}
		},
		async onFileChanged(e, file = null) {
			if(!this.draftId && !this.draftCreated) await this.createDraft()
			const newFile = e?.target?.files[0] || file
			if(!newFile) return
			const uuid = uuidv4()
			if(newFile.type.includes('image')) { const base64File = await this.toBase64(newFile); this.filesPreview.push({src: base64File, name: newFile.name, uuid}) }
			else this.filesPreview.push({noImage: true, name: newFile.name, uuid})
			this.$store.globalStore.commit('setFilesToUploadCenter', { type: 13, payload: {draft_id: this.draftId, project_id: this.$store.projectsStore.state.selectedProject.id, uuid}, description: this.newRDIForm.title, total: newFile.size, uuid, file: newFile, attach_files: this.attachFiles, copy_to: this.newRDIForm.copy_to })
		},
		removeAttachment(file, index) { this.filesPreview.splice(index, 1); if(file.id) deleteFile({file_id: file.id}) },
		checkFilesAttach() {
			if(this.filesPreview.length > 0) this.disabledAttachSelector = false
			else { this.disabledAttachSelector = true; this.attachFiles = false }
		},
		fileListeners() {
			EventBus.$on('completedFileUpload', async (payload) => {
				if(payload && payload.draft_id === this.draftId) {
					const findIndex = this.filesPreview.findIndex(f => f.uuid === payload.uuid)
					if(findIndex !== -1) this.$set(this.filesPreview[findIndex], 'id', payload.file.id)
				}
			})
		},
		toBase64(file) {
			return new Promise(resolve => { const r = new FileReader(); r.readAsDataURL(file); r.onload = () => resolve(r.result); r.onerror = () => resolve(null) })
		},
		preCreateRdi() {
			if(this.inCancelTime) { clearInterval(this.cancelInterval); this.confirmButtonText = 'Confirmar'; this.inCancelTime = false; return null }
			if(!this.newRDIForm.description) {
				this.$store.globalStore.dispatch('addMessage', { message: 'Por favor escribe una descripción.', title: 'Error', created: new Date(), type: 'success', icon: 'mdi-alert-circle', color: '#fb8c00' })
				return false
			}
			if(this.$refs.form.validate()) {
				this.inCancelTime = true; this.cancelTimer = 7; this.confirmButtonText = `Cancelar (${this.cancelTimer})`
				this.cancelInterval = setInterval(() => {
					this.cancelTimer--; this.confirmButtonText = `Cancelar (${this.cancelTimer})`
					if(this.cancelTimer == 0) { this.inCancelTime = false; clearInterval(this.cancelInterval); this.createRdi() }
				}, 1000)
			}
		},
		async saveDraftAndClose() {
			try {
				this.loadingData = true
				await this.updateDraft()
				this.$store.globalStore.dispatch('addMessage', {
					message: 'Borrador guardado correctamente.',
					title: 'Borrador Guardado',
					type: 'success',
					icon: 'mdi-check-circle',
					color: '#3EBFA3',
					created: new Date()
				})
				this.closeModalSelf()
			} catch(error) {
				console.error('Error al guardar borrador:', error)
				this.$store.globalStore.dispatch('addMessage', {
					message: 'Error al guardar el borrador. Por favor, intenta de nuevo.',
					title: 'Error',
					type: 'error',
					icon: 'mdi-alert-circle',
					color: '#fb8c00',
					created: new Date()
				})
			} finally {
				this.loadingData = false
			}
		},
		async createRdi() {
			this.loadingData = true; this.confirmButtonText = 'Confirmar'
			if(this.especialidades.length > 0) this.newRDIForm.especialidades = JSON.stringify(this.especialidades)
			// Serialización de `copy_to` comentada por petición
			// if(this.copy_to.length > 0) {
			// 	this.newRDIForm.copy_to = JSON.stringify(this.copy_to.map(e => e.email ? {email: e.email, name: e.name} : {email: e, name: ''}))
			// }
			// Serializar subscriptores (solo IDs)
			if(this.subscriptions.length > 0) this.newRDIForm.subscribers = JSON.stringify(this.subscriptions)
			else this.newRDIForm.subscribers = null
			if(this.newRDIForm.subject?.id) this.newRDIForm.subject_id = this.newRDIForm.subject.id
			else if(this.newRDIForm.subject) this.newRDIForm.new_subject = this.newRDIForm.subject

			const hasReminders = this.newRDIForm.reminder.length > 0
			this.newRDIForm.reminder = JSON.stringify(this.newRDIForm.reminder)
			this.newRDIForm.project_id = this.$store.projectsStore.state.selectedProject.id
			this.newRDIForm.draft_id = this.draftId

			const associations = [...this.associations]
			this.newRDIForm.associations = JSON.stringify(associations)

			const formData = new FormData()
			Object.keys(this.newRDIForm).forEach(key => { if(this.newRDIForm[key]) formData.append(key, this.newRDIForm[key]) })
			if(this.newInteractionMentions.length > 0) this.newInteractionMentions.forEach(m => formData.append('mentions[]', m))
			const rdi = await create(formData)
			if(rdi.code === 200) {
				if(this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
				const checkSendEmailData = { type: 3, item_id: rdi.rdi.id, copy_to: this.newRDIForm.copy_to, attach_files: this.attachFiles }
				this.$store.globalStore.dispatch('checkSendingEmail', checkSendEmailData)
				await this.getRdiList()
				this.closeModalSelf()

				//Verificar si se debe cargar una tarea previamente cargada (asociación)
				if(associations.length > 0){
					associations.forEach(association => {
						EventBus.$emit('reload_element', association)
					});
				}
				// Se cambia la llamada directo por el evento, así sólo será escuchado si el componente está montado (evita llamar a la api innecesariamente)
				// this.$store.projectsStore.dispatch('getDashboardInfo');
				EventBus.$emit('onProjectElementsChange')

				if(rdi.task) this.checkReloadTask(rdi.task)
				const message = 'Haz click aquí para visualizarla.'
				this.$store.globalStore.dispatch('addMessage', { message, title: 'Se ha creado la RDI ' + rdi.rdi.number, created: new Date(), type: 'success', icon: 'mdi-check-circle', color: '#3EBFA3', action: 'rdi', rdi_id: rdi.rdi.id })
				hasReminders && this.$store.notificationsStore.dispatch('getReminders')
				this.viewRDI(rdi.rdi.id, false)
			}
			else{
				this.saveDraftAndClose()
			}
			this.loadingData = false
		},
		checkReloadTask(task) {
			if(this.$store.consultasStore.state.currentTarea && this.$store.consultasStore.state.currentTarea.id == task.id) {
				this.$store.consultasStore.commit('setCurrentTarea', task)
			}
		},
		async viewRDI(rdi_id, seen_by_me){
			this.$store.rdiStore.commit('setCurrentRDI', {loading: true})
			const rdi = await getRdiByID({rdi_id})
			if(rdi.code === 200){
				let rdiData = rdi.rdi
				rdiData.prev_seen_by_me = seen_by_me
				this.$store.rdiStore.commit('setCurrentRDI', rdiData)
			}
		},
	}
}
</script>

<style scoped>
/* ── Modal Card ── */
.modal-card {
	border-radius: 12px !important;
	overflow: hidden;
}

/* ── Header ── */
.modal-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 14px 20px;
	background: #2D4B95;
	border-bottom: 1px solid rgba(255,255,255,0.1);
}
.modal-header-left {
	display: flex;
	align-items: center;
	gap: 10px;
}
.modal-icon {
	width: 30px;
	height: 30px;
	border-radius: 8px;
	background: rgba(255,255,255,0.15);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}
.modal-title {
	margin: 0;
	font-size: 14px;
	font-weight: 600;
	color: white;
	line-height: 1.2;
}
.modal-subtitle {
	margin: 0;
	font-size: 11px;
	color: rgba(255,255,255,0.65);
	line-height: 1.2;
}
.modal-subtitle .separator {
	margin: 0 5px;
	opacity: 0.5;
}
.modal-header-actions {
	display: flex;
	gap: 6px;
}
.header-btn {
	width: 26px;
	height: 26px;
	border-radius: 6px;
	border: none;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background 0.15s;
}
.header-btn.minimize { background: rgba(251, 140, 0, 0.9); color: white; }
.header-btn.minimize:hover { background: rgba(251, 140, 0, 1); }
.header-btn.close { background: rgba(244, 81, 108, 0.9); color: white; }
.header-btn.close:hover { background: rgba(244, 81, 108, 1); }

/* ── Body ── */
.modal-body {
	overflow-y: auto;
	max-height: calc(90vh - 80px);
}

/* ── Form Sections ── */
.form-section {
	padding: 14px 20px;
	border-bottom: 1px solid #F3F4F6;
}
.form-section:last-child { border-bottom: none; }

.field-label {
	display: block;
	font-size: 11px;
	font-weight: 600;
	color: #2D4B95;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	margin-bottom: 6px;
}

.section-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 6px;
}

/* ── Grids ── */
.fields-grid {
	display: grid;
	gap: 12px;
}
.fields-grid.two-col { grid-template-columns: 1fr 1fr; }
.fields-grid.three-col { grid-template-columns: 1fr 1fr 1fr; }
.field-group { display: flex; flex-direction: column; }

@media (max-width: 600px) {
	.fields-grid.two-col,
	.fields-grid.three-col { grid-template-columns: 1fr; }
}

/* ── Priority ── */
.priority-group {
	display: flex;
	flex-direction: column;
	gap: 4px;
}
.priority-btn {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 5px 10px;
	border-radius: 6px;
	border: 1.5px solid var(--p-color, #ccc);
	background: transparent;
	cursor: pointer;
	font-size: 12px;
	font-weight: 500;
	color: var(--p-color, #666);
	transition: all 0.15s;
	white-space: nowrap;
}
.priority-btn .priority-dot {
	width: 7px;
	height: 7px;
	border-radius: 50%;
	background: var(--p-color, #ccc);
	opacity: 0.5;
	flex-shrink: 0;
	transition: opacity 0.15s;
}
.priority-btn.active { background: var(--p-color); color: white; }
.priority-btn.active .priority-dot { opacity: 1; background: white; }

/* ── Weekdays ── */
.weekday-group {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
}
.weekday-btn {
	width: 30px;
	height: 28px;
	border-radius: 6px;
	border: 1.5px solid #E5E7EB;
	background: transparent;
	cursor: pointer;
	font-size: 11px;
	font-weight: 500;
	color: #6B7280;
	transition: all 0.15s;
}
.weekday-btn.active {
	background: #5E85EF;
	border-color: #5E85EF;
	color: white;
}

/* ── Attachments ── */
.attachments-row {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 8px;
	flex-wrap: wrap;
}
.previews { display: flex; gap: 6px; flex-wrap: wrap; flex: 1; }
.preview-img-wrap { position: relative; }
.preview-thumb { width: 40px; height: 40px; border-radius: 6px; object-fit: cover; display: block; }
.preview-remove {
	position: absolute;
	top: -4px; right: -4px;
	width: 16px; height: 16px;
	border-radius: 50%;
	background: #F4516C;
	border: none;
	cursor: pointer;
	display: flex; align-items: center; justify-content: center;
}
.preview-file {
	display: flex; align-items: center; gap: 4px;
	padding: 3px 8px;
	border-radius: 5px;
	background: #EEF2FF;
	font-size: 11px;
	color: #4B5563;
}
.preview-file button { background: none; border: none; cursor: pointer; display: flex; align-items: center; }
.attach-btn {
	display: flex; align-items: center; gap: 5px;
	padding: 5px 12px;
	border-radius: 6px;
	border: 1.5px solid #E5E7EB;
	background: white;
	cursor: pointer;
	font-size: 12px;
	color: #5E85EF;
	font-weight: 500;
	transition: border-color 0.15s;
	white-space: nowrap;
}
.attach-btn:hover { border-color: #5E85EF; }

/* ── Toggle Pill ── */
.toggle-pill {
	display: inline-flex; align-items: center; gap: 4px;
	padding: 4px 10px;
	border-radius: 20px;
	border: 1.5px solid #E5E7EB;
	background: white;
	cursor: pointer;
	font-size: 11px;
	color: #6B7280;
	font-weight: 500;
	transition: all 0.15s;
}
.toggle-pill.active { border-color: #2D4B95; background: #EEF2FF; color: #2D4B95; }
.toggle-pill:disabled { opacity: 0.4; cursor: not-allowed; }

/* ── Similares ── */
.similares-btn {
	display: inline-flex;
	align-items: center;
	gap: 5px;
	padding: 3px 8px 3px 5px;
	border-radius: 20px;
	border: none;
	cursor: pointer;
	font-size: 11px;
	font-weight: 600;
	line-height: 1;
	white-space: nowrap;
	transition: background 0.15s, transform 0.1s;
	background: #EEF2FC;
	color: #2D4B95;
}
.similares-btn:hover { background: #dde6f8; }
.similares-btn:active { transform: scale(0.97); }
.similares-btn.is-open { background: #2D4B95; color: #fff; }
.similares-btn.is-open .similares-count { background: rgba(255,255,255,0.25); color: #fff; }
.similares-btn.is-open .similares-chevron { color: rgba(255,255,255,0.9) !important; }
.similares-count {
	width: 18px; height: 18px;
	border-radius: 50%;
	background: #2D4B95;
	color: #fff;
	font-size: 10px;
	font-weight: 700;
	display: flex; align-items: center; justify-content: center;
	flex-shrink: 0;
	transition: background 0.15s, color 0.15s;
}
.similares-chevron { color: #2D4B95 !important; transition: color 0.15s; }

/* ── Footer ── */
.modal-footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 20px;
	background: #F9FAFB;
	border-top: 1px solid #E5E7EB;
}
.footer-actions { display: flex; gap: 8px; align-items: center; }
.btn-cancel {
	padding: 7px 16px;
	border-radius: 7px;
	border: 1.5px solid #E5E7EB;
	background: white;
	cursor: pointer;
	font-size: 13px;
	font-weight: 500;
	color: #374151;
	transition: all 0.15s;
}
.btn-cancel:hover { border-color: #D1D5DB; background: #F3F4F6; }
.btn-confirm {
	padding: 7px 20px;
	border-radius: 7px;
	border: none;
	background: #2D4B95;
	cursor: pointer;
	font-size: 13px;
	font-weight: 600;
	color: white;
	display: flex; align-items: center;
	transition: all 0.15s;
}
.btn-confirm:hover { background: #253d7a; }
.btn-confirm.canceling { background: #F4516C; }
.btn-confirm:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Misc ── */
.light-blue-text { color: #5E85EF; }

/* ── Participants ── */
.participant-list { display: flex; flex-direction: column; gap: 4px; }
.participant-card {
	display: flex; align-items: center; justify-content: space-between;
	padding: 6px 10px;
	background: #F9FAFB;
	border: 1px solid #E5E7EB;
	border-radius: 8px;
	gap: 8px;
}
.participant-left { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0; }
.p-avatar {
	width: 30px; height: 30px; border-radius: 50%;
	background: #E0E7FF;
	display: flex; align-items: center; justify-content: center;
	font-size: 11px; font-weight: 600; color: #4F46E5;
	flex-shrink: 0; overflow: hidden;
}
.p-avatar img { width: 100%; height: 100%; object-fit: cover; }
.p-info { display: flex; flex-direction: column; min-width: 0; }
.p-name { font-size: 12px; font-weight: 500; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.p-role { font-size: 11px; color: #6B7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.participant-right { display: flex; align-items: center; gap: 5px; flex-shrink: 0; }
.p-date-btn {
	display: flex; align-items: center; gap: 3px;
	padding: 3px 7px;
	border: 1px solid #D1D5DB;
	border-radius: 5px;
	background: white;
	cursor: pointer;
	font-size: 11px;
	color: #374151;
	white-space: nowrap;
	transition: border-color 0.15s;
}
.p-date-btn:hover { border-color: #2D4B95; }
.p-remove-btn {
	display: flex; align-items: center; justify-content: center;
	padding: 3px; border: none; background: transparent; cursor: pointer;
	border-radius: 4px; transition: background 0.15s;
}
.p-remove-btn:hover { background: #FEE2E2; }

::v-deep .remove-bottom-details .v-text-field__details { display: none; }
::v-deep .remove-bottom-details .v-input__slot { margin-bottom: 0; }
</style>